import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  "aria-label": string;
}

/** A pressed-button group in the look of the recipe card's layout and batch toggles. */
export function SegmentedControl<T extends string>({ value, onChange, options, "aria-label": ariaLabel }: SegmentedControlProps<T>) {
  return (
    <div role="group" aria-label={ariaLabel} className="flex items-center gap-1 rounded-xl bg-(--color-surface-muted) p-1">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            "inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors",
            value === option.value
              ? "bg-(--color-surface-sidebar) text-(--color-text-on-dark)"
              : "text-(--color-text-secondary) hover:text-(--color-text-primary)",
          )}
        >
          {option.icon}
          {option.label}
        </button>
      ))}
    </div>
  );
}
